import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Target,
  CalendarClock,
  Settings,
  LogOut,
  Menu,
  X,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/app/crm", label: "Dashboard", end: true, icon: LayoutDashboard, testId: "crm-nav-dashboard" },
  { to: "/app/crm/contacts", label: "Contacts", icon: Users, testId: "crm-nav-contacts" },
  { to: "/app/crm/companies", label: "Companies", icon: Building2, testId: "crm-nav-companies" },
  { to: "/app/crm/deals", label: "Deals", icon: Target, testId: "crm-nav-deals" },
  { to: "/app/crm/activities", label: "Activities", icon: CalendarClock, testId: "crm-nav-activities" },
  { to: "/app/crm/settings", label: "Settings", icon: Settings, testId: "crm-nav-settings" },
];

function SidebarContent({ onClose }) {
  const { user, workspace, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="h-full flex flex-col bg-[#FDFCFB] border-r border-[#E8E6E1]">
      <div className="px-6 pt-6 pb-8 border-b border-[#E8E6E1] flex items-center justify-between">
        <Link
          to="/app/crm"
          onClick={onClose}
          className="font-serif-display text-2xl tracking-tight text-[#1A1A1A]"
        >
          Heritex<span className="text-[#6B6A68]">.</span>
          <span className="ml-1 font-mono-tech text-[10px] tracking-[0.18em] uppercase text-[#6B6A68] align-middle">
            CRM
          </span>
        </Link>
        {onClose && (
          <button onClick={onClose} className="md:hidden p-1.5">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="px-3 py-6 flex-1 overflow-y-auto">
        <div className="caption !text-[10px] px-3 mb-3">Workspace</div>
        <div className="px-3 mb-6">
          <div className="font-serif-display text-[15px] text-[#1A1A1A] leading-tight">
            {workspace?.name || "—"}
          </div>
          {workspace?.pack_slug && (
            <div className="font-mono-tech text-[10px] tracking-[0.16em] uppercase text-[#6B6A68] mt-1.5">
              {workspace.pack_slug.replace(/-/g, " ")}
            </div>
          )}
        </div>

        <div className="caption !text-[10px] px-3 mb-2">Navigate</div>
        <nav className="space-y-0.5">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={onClose}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-[#1A1A1A] text-[#FDFCFB]"
                      : "text-[#1A1A1A] hover:bg-[#F5F4F0]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.6} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="border-t border-[#E8E6E1] p-4">
        <div className="px-2 py-2">
          <div className="text-[13px] text-[#1A1A1A] truncate">
            {user?.name || user?.email}
          </div>
          <div className="text-[11px] text-[#6B6A68] truncate">{user?.email}</div>
        </div>
        <button
          onClick={async () => {
            await logout();
            navigate("/login", { replace: true });
          }}
          data-testid="crm-logout"
          className="mt-2 w-full inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-[#1A1A1A] hover:bg-[#F5F4F0] transition-colors"
        >
          <span className="inline-flex items-center gap-2.5">
            <LogOut className="w-4 h-4" strokeWidth={1.6} /> Sign out
          </span>
          <ArrowUpRight className="w-3.5 h-3.5 text-[#6B6A68]" />
        </button>
      </div>
    </div>
  );
}

export default function CrmLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FDFCFB] text-[#1A1A1A] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:block fixed inset-y-0 left-0 w-[260px] z-30">
        <SidebarContent />
      </aside>

      {/* Sidebar (mobile drawer) */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-[#1A1A1A]/40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[260px]">
            <SidebarContent onClose={() => setOpen(false)} />
          </div>
        </div>
      )}

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-20 h-14 bg-[#FDFCFB]/95 backdrop-blur-md border-b border-[#E8E6E1] flex items-center justify-between px-4">
        <button onClick={() => setOpen(true)} className="p-1.5">
          <Menu className="w-5 h-5" />
        </button>
        <div className="font-serif-display text-lg tracking-tight">
          Heritex<span className="text-[#6B6A68]">.</span>
        </div>
        <div className="w-7" />
      </header>

      {/* Main */}
      <main className="flex-1 md:ml-[260px] pt-14 md:pt-0 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
